import { Nullable, NullableResultPromise } from "../../base-types";
import { DEFINITIONS } from "../../constants";
import CultureInfo from "../../culture-info";
import { FinalLifeCycleStatusMapper } from "../../mappers/final-life-cycle-status-mapper";
import { Entity } from "../base/entity";
import { IChildToOneParentRelation } from "../base/relations/child-to-one-parent-relation";
import { ITypedEntity } from "../base/typed-entity";
import { CultureLoadOption } from "../querying/culture-load-option";
import { EntityLoadConfiguration } from "../querying/entity-load-configuration";
import { PropertyLoadOption } from "../querying/property-load-option";
import { RelationLoadOption } from "../querying/relation-load-option";
import { FinalLifeCycleStatus } from "./final-life-cycle-status";

export interface IAsset extends ITypedEntity {
    title: Nullable<string>;
    fileName: Nullable<string>;

    getDescription(culture: CultureInfo): Nullable<string>;
    setDescription(culture: CultureInfo, description: Nullable<string>): void;

    getFinalLifeCycleStatusAsync(): NullableResultPromise<FinalLifeCycleStatus>;
}

export class Asset extends Entity implements IAsset {
    get title(): Nullable<string> {
        return this.getPropertyValue<"String">(DEFINITIONS["Asset"].title);
    }

    set title(value: Nullable<string>) {
        this.setPropertyValue(DEFINITIONS["Asset"].title, value);
    }

    get fileName(): Nullable<string> {
        return this.getPropertyValue<"String">(DEFINITIONS["Asset"].fileName);
    }

    set fileName(value: Nullable<string>) {
        this.setPropertyValue(DEFINITIONS["Asset"].fileName, value);
    }

    getDescription(culture: CultureInfo): Nullable<string> {
        return this.getPropertyValue<"String">(DEFINITIONS["Asset"].description, culture);
    }

    setDescription(culture: CultureInfo, description: Nullable<string>): void {
        return this.setPropertyValue(DEFINITIONS["Asset"].description, description, culture);
    }

    async getFinalLifeCycleStatusAsync(): NullableResultPromise<FinalLifeCycleStatus> {
        const relation = this.getRelation(
            DEFINITIONS["Asset"].finalLifeCycleStatusToAsset
        ) as IChildToOneParentRelation;

        if (relation == null || relation.parent == null) {
            return null;
        }

        const loadConfiguration = new EntityLoadConfiguration(
            CultureLoadOption.None,
            new PropertyLoadOption(DEFINITIONS["FinalLifeCycleStatus"].statusValue),
            RelationLoadOption.None
        );

        const parent = await this._client.entities.getAsync(relation.parent, loadConfiguration);

        if (parent == null) {
            return null;
        }

        const statusString = parent.getPropertyValue<"String">(DEFINITIONS["FinalLifeCycleStatus"].statusValue);
        const status = FinalLifeCycleStatusMapper.fromString(statusString);

        return status;
    }

    async loadTypedMembersAsync(): Promise<void> {
        const propertyLoadOption = new PropertyLoadOption([
            DEFINITIONS["Asset"].title,
            DEFINITIONS["Asset"].fileName,
            DEFINITIONS["Asset"].description,
        ]);

        const relationLoadOption = new RelationLoadOption([DEFINITIONS["Asset"].finalLifeCycleStatusToAsset]);

        await this.loadMembersAsync(propertyLoadOption, relationLoadOption);
    }
}
